// Is anyone in this SMART ROOM? — the person image on a room (both shells).
//
// A smart room is a room with an RF DOOR sensor and an RF MOTION sensor. The
// server decides (Home Automation security.js, smartRoomStateSql) and this
// file only reads its verdict — `Presence` from GetSmartRoomState:
//
//   1     door closed, motion more than 5 s after it closed  → OCCUPIED (person shown)
//   0     door closed, no motion for 5 min (lights go off)   → UNOCCUPIED (nothing)
//   null  door open, still inside the 5 min, or a dead PIR    → UNKNOWN (nothing)
//
// ⚠️ ONLY THIS VERDICT. `Rooms.PresenceState` on an ordinary room is the
// night-lock "anyone inside" judgement (docs/18) — a different question with
// different rules — so it is never used for the image. A room that is not a
// smart room has no row here and never shows a person.

import { ageOf } from './deviceStatus';
import { parseServerTime } from './firmwareInfo';

const asInt = (v) => { const n = parseInt(String(v ?? ''), 10); return Number.isNaN(n) ? null : n; };

/** Server timestamp -> ms since then, or null. (IST labelled as UTC — ageOf undoes it.) */
const ageMs = (raw) => {
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ageOf(ms);
};

/**
 * GetSmartRoomState rows -> Map(RoomID -> presence), for one location (or all
 * when locationId is null). presence = { occupied, known, motionAgeMs, doorAgeMs,
 * doorEvent, locationId, roomName, locked, lockedAt (ms, real time), lockedBy,
 * lockSource, openIncidents, settings }.
 *
 * ⚠️ A ROW HERE = A SMART ROOM (door + motion sensor). The room lock, its alarm
 * and the night settings exist ONLY for such a room (owner, 2026-10-12), so the
 * screens show them only when this map has the room.
 */
export function presenceByRoom(rows, locationId = null) {
  const out = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (locationId != null && String(r.LocationID) !== String(locationId)) return;
    const v = asInt(r.Presence);
    out.set(String(r.RoomID), {
      occupied: v === 1,
      known: v === 0 || v === 1,
      motionAgeMs: ageMs(r.MotionAt),
      motionAt: parseServerTime(r.MotionAt),   // real ms — compared with lockedAt
      doorAgeMs: ageMs(r.DoorAt),
      doorEvent: String(r.DoorEvent || ''),
      locationId: r.LocationID ?? null,
      roomName: String(r.RoomName || ''),
      locked: asInt(r.RoomLock) === 1,
      lockedAt: parseServerTime(r.RoomLockedAt),
      lockedBy: String(r.RoomLockedByName || '').trim(),
      lockSource: String(r.RoomLockSource || ''),
      openIncidents: asInt(r.OpenIncidents) || 0,
      settings: {
        // false = the server has not had smart_room_lock_migration.sql yet
        available: Object.prototype.hasOwnProperty.call(r, 'NightFrom'),
        nightFrom: String(r.NightFrom || ''),
        nightTo: String(r.NightTo || ''),
        autoLock: asInt(r.AutoLock) === 1,
        sleepAfterMin: asInt(r.SleepAfterMin) || 0,
        doorOpenAlertMin: asInt(r.DoorOpenAlertMin) || 0,
      },
    });
  });
  return out;
}

const ago = (ms) => {
  if (ms == null) return null;
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
};

// ── VACANT: counted from the ROOM LOCK (owner, 2026-10-15) ──────────────────
// "After Room Lock we check for motion between 5 seconds and 300 seconds; if
// there is no motion, no one is inside."
//   0–5 s after the lock     motion ignored (the person stepping out, the door)
//   5–300 s, no motion yet    'checking'
//   300 s passed, no motion   'vacant'  — the vacant symbol
//   motion after lock + 5 s   'occupied' — the person (whatever the door verdict)
//   not locked                null — the server's door verdict alone, as before
// ⚠️ This decides only the SYMBOL. The server's lights-off rule (door close +
// 300 s, security.js smartRoomStateSql) and the intrusion watch are untouched.
export const LOCK_IGNORE_MS = 5 * 1000;
export const LOCK_VACANT_MS = 300 * 1000;

/** 'vacant' | 'occupied' | 'checking' | null (not locked / no lock time). */
export function lockVerdict(p, now = Date.now()) {
  if (!p || !p.locked || !p.lockedAt) return null;
  if (p.motionAt != null && p.motionAt > p.lockedAt + LOCK_IGNORE_MS) return 'occupied';
  return now >= p.lockedAt + LOCK_VACANT_MS ? 'vacant' : 'checking';
}

/** The person: the lock rule while locked, the server's door verdict otherwise. */
export function isOccupied(p, now = Date.now()) {
  const v = lockVerdict(p, now);
  if (v === 'occupied') return true;
  if (v === 'vacant') return false;
  return !!(p && p.occupied);
}

export const isVacant = (p, now = Date.now()) => lockVerdict(p, now) === 'vacant';

/** "Vacant — locked 18 min ago, no motion since" (or null when not vacant). */
export function vacantCaption(p, now = Date.now()) {
  if (!isVacant(p, now)) return null;
  return `Vacant — locked ${ago(Math.max(0, now - p.lockedAt))}, no motion since`;
}

/** "Occupied — motion 2 min ago, door closed 12 min ago" (or null when not occupied). */
export function presenceCaption(p, now = Date.now()) {
  if (!p || !isOccupied(p, now)) return null;
  const parts = [];
  if (p.motionAgeMs != null) parts.push(`motion ${ago(p.motionAgeMs)}`);
  if (p.doorAgeMs != null) parts.push(`door closed ${ago(p.doorAgeMs)}`);
  return parts.length ? `Occupied — ${parts.join(', ')}` : 'Occupied';
}

const clock = (ms) => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** "Locked by Sanjay · since 10:42 PM" / "Locked for the night · since 11:00 PM" / "Unlocked". */
export function lockCaption(p, now = Date.now()) {
  if (!p) return '';
  if (!p.locked) return 'Unlocked';
  const who = p.lockSource === 'schedule' ? 'Locked for the night' : `Locked${p.lockedBy ? ` by ${p.lockedBy}` : ''}`;
  if (!p.lockedAt) return who;
  const sameDay = new Date(now).toDateString() === new Date(p.lockedAt).toDateString();
  const when = sameDay ? clock(p.lockedAt) : `${new Date(p.lockedAt).toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${clock(p.lockedAt)}`;
  return `${who} · since ${when}`;
}

/** "Door closed 12 min ago · motion 2 min ago" — the live line, whatever the verdict. */
export function sensorsCaption(p) {
  if (!p) return '';
  const parts = [];
  const ev = String(p.doorEvent || '').toLowerCase();
  if (ev === 'open' || ev === 'close') parts.push(`Door ${ev === 'open' ? 'open' : 'closed'} ${ago(p.doorAgeMs) || ''}`.trim());
  if (p.motionAgeMs != null) parts.push(`motion ${ago(p.motionAgeMs)}`);
  return parts.join(' · ');
}

// ── VACANT, with WHEN (owner, 2026-10-18: "show the room is vacant, and since
// when — e.g. 100 min") ──────────────────────────────────────────────────────
// Vacant = the lock rule says 'vacant' (above), OR the room is unlocked and the
// server's door verdict is UNOCCUPIED (Presence 0: door closed, no motion after
// it). Never while occupied, the door is open, or the verdict is unknown.
// SINCE = when the last person left: the later of the last motion and the door
// closing. (Locked rooms keep isVacant/lockVerdict for the symbol rules above.)
/** -> { vacant: true, since (ms), forMs } | { vacant: false } */
export function roomVacancy(p, now = Date.now()) {
  if (!p || isOccupied(p, now)) return { vacant: false };
  const v = lockVerdict(p, now);
  const vacant = v === 'vacant' || (v === null && p.known && !p.occupied);
  if (!vacant) return { vacant: false };
  const doorAt = p.doorAgeMs != null ? now - p.doorAgeMs : null;
  const times = [p.motionAt, doorAt].filter((t) => Number.isFinite(t));
  const since = times.length ? Math.max(...times) : (p.lockedAt || null);
  return { vacant: true, since, forMs: since != null ? Math.max(0, now - since) : null };
}

/** "1 h 40 min" / "25 min" / "3 d 2 h". */
export function vacantFor(ms) {
  if (ms == null) return '';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h${min % 60 ? ` ${min % 60} min` : ''}`;
  const d = Math.floor(h / 24);
  return `${d} d${h % 24 ? ` ${h % 24} h` : ''}`;
}
